import axios, { AxiosError } from 'axios';
import { api } from '@/utils/api';
import { API_ENDPOINTS } from '@/constants/api.constants';
import type { ApiResponse } from '@/types/api';
import { logger } from '@/utils/logger';
import { AppError, ErrorCodes } from '@/utils/errors';

export interface Federation {
    id: number;
    name: string;
    country: string;
    region: string | null;
    city: string | null;
    president: string | null;
    description: string | null;
    phone: string | null;
    email: string | null;
    website: string | null;
    address: string | null;
    logo: string | null;
    status: 'active' | 'inactive';
    createdAt: Date;
    updatedAt: Date;
}

export interface UpdateFederationDTO {
    name?: string;
    country?: string;
    region?: string;
    city?: string;
    president?: string;
    description?: string;
    phone?: string;
    email?: string;
    website?: string;
    address?: string;
    status?: 'active' | 'inactive';
}

export interface CreateFederationDTO {
    name: string;
    country: string;
    region?: string | null;
    city?: string | null;
    president?: string | null;
    description?: string | null;
    phone?: string | null;
    email?: string | null;
    website?: string | null;
    address?: string | null;
    logo?: string | null;
}

interface FederationQueryParams {
    page?: number;
    limit?: number;
    search?: string;
    country?: string;
    region?: string;
    status?: 'active' | 'inactive';
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
}

interface FederationPaginatedResponse {
    items: Federation[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export class FederationService {
    static async createFederation(data: CreateFederationDTO): Promise<Federation> {
        try {
            // Токены теперь в httpOnly cookies, они отправляются автоматически
            const response = await api.post<ApiResponse<Federation>>(
                API_ENDPOINTS.FEDERATIONS.CREATE,
                data
            );
            return response.data.data;
        } catch (error) {
            logger.error('Error creating federation:', error);

            if (axios.isAxiosError(error)) {
                const axiosError = error as AxiosError<any>;

                if (!axiosError.response) {
                    throw new AppError(
                        'Ошибка сети. Проверьте подключение к интернету.',
                        503,
                        ErrorCodes.NETWORK_ERROR
                    );
                }

                const status = axiosError.response.status;
                const responseData = axiosError.response.data;

                if (status === 401) {
                    // Токены в httpOnly cookies, редирект обрабатывается в AuthContext
                    throw new AppError(
                        'Необходима авторизация',
                        401,
                        ErrorCodes.UNAUTHORIZED
                    );
                }

                throw new AppError(
                    responseData?.message || 'Ошибка при создании федерации',
                    status,
                    ErrorCodes.INTERNAL_ERROR
                );
            }

            throw new AppError(
                'Ошибка при создании федерации',
                500,
                ErrorCodes.INTERNAL_ERROR
            );
        }
    }

    static async updateFederation(id: number, data: UpdateFederationDTO): Promise<Federation> {
        try {
            // Токены теперь в httpOnly cookies, они отправляются автоматически

            // Clean the data before sending
            const cleanedData: Partial<UpdateFederationDTO> = {};
            Object.entries(data).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    cleanedData[key as keyof UpdateFederationDTO] = value;
                }
            });

            logger.info(`Updating federation ${id} with data:`, cleanedData);

            const response = await api.put<ApiResponse<Federation>>(
                API_ENDPOINTS.FEDERATIONS.UPDATE(id),
                cleanedData
            );

            // Проверяем, что ответ содержит данные
            if (response.data && response.data.data) {
                // Если статус не указан или успешный, возвращаем данные
                if (!response.data.status || response.data.status === 'success') {
                    return response.data.data;
                }
            }

            // Если дошли до сюда, значит что-то пошло не так
            logger.warn('Unexpected response format:', response.data);
            throw new AppError(
                response.data?.message || 'Ошибка при обновлении федерации',
                response.status,
                ErrorCodes.INTERNAL_ERROR
            );

            return response.data.data;
        } catch (error) {
            logger.error('Error updating federation:', error);

            if (axios.isAxiosError(error)) {
                const axiosError = error as AxiosError<any>;

                if (!axiosError.response) {
                    throw new AppError(
                        'Ошибка сети. Проверьте подключение к интернету.',
                        503,
                        ErrorCodes.NETWORK_ERROR
                    );
                }

                const status = axiosError.response.status;
                const responseData = axiosError.response.data;

                switch (status) {
                    case 400:
                        throw new AppError(
                            responseData?.message || 'Неверные данные',
                            400,
                            ErrorCodes.BAD_REQUEST
                        );
                    case 401:
                        // Токены в httpOnly cookies, редирект обрабатывается в AuthContext
                        throw new AppError(
                            'Необходима авторизация',
                            401,
                            ErrorCodes.UNAUTHORIZED
                        );
                    case 404:
                        throw new AppError(
                            'Федерация не найдена. Возможно, она была удалена.',
                            404,
                            ErrorCodes.NOT_FOUND
                        );
                    case 409:
                        throw new AppError(
                            'Федерация с таким названием уже существует',
                            409,
                            ErrorCodes.CONFLICT
                        );
                    default:
                        throw new AppError(
                            responseData?.message || 'Ошибка при обновлении федерации',
                            status,
                            ErrorCodes.INTERNAL_ERROR
                        );
                }
            }

            throw new AppError(
                'Ошибка при обновлении федерации',
                500,
                ErrorCodes.INTERNAL_ERROR
            );
        }
    }

    static async getFederation(id: number): Promise<Federation> {
        try {
            // Токены теперь в httpOnly cookies, они отправляются автоматически
            const response = await api.get<ApiResponse<Federation>>(
                API_ENDPOINTS.FEDERATIONS.GET(id)
            );
            return response.data.data;
        } catch (error) {
            // Handle errors similar to updateFederation
            throw error;
        }
    }

    static async getFederations(params: FederationQueryParams = {}): Promise<FederationPaginatedResponse> {
        try {
            // Токены теперь в httpOnly cookies, они отправляются автоматически
            const response = await api.get<ApiResponse<FederationPaginatedResponse>>(
                API_ENDPOINTS.FEDERATIONS.BASE,
                {
                    params
                }
            );            

            // Проверяем структуру ответа
            if (!response.data) {
                logger.error('No data in response:', response);
                throw new AppError(
                    'Получен некорректный ответ от сервера: нет данных',
                    500,
                    ErrorCodes.INTERNAL_ERROR
                );
            }

            // Структура ответа: { success: true, data: { items: [...], total: ..., ... } }
            if (response.data.data) {                
                return response.data.data;
            }

            // Если data нет, но есть status: 'error'
            if (response.data.status === 'error') {
                logger.error('API returned status: error:', response.data);
                throw new AppError(
                    response.data.message || 'Получен некорректный ответ от сервера',
                    500,
                    ErrorCodes.INTERNAL_ERROR
                );
            }

            logger.error('Unexpected response structure:', response.data);
            throw new AppError(
                'Получен некорректный ответ от сервера: неожиданная структура',
                500,
                ErrorCodes.INTERNAL_ERROR
            );
        } catch (error) {
            logger.error('Error fetching federations:', error);

            if (axios.isAxiosError(error)) {
                const axiosError = error as AxiosError<any>;

                if (!axiosError.response) {
                    throw new AppError(
                        'Ошибка сети. Проверьте подключение к интернету.',
                        503,
                        ErrorCodes.NETWORK_ERROR
                    );
                }

                const status = axiosError.response.status;
                const responseData = axiosError.response.data;

                if (status === 401) {
                    // Токены в httpOnly cookies, редирект обрабатывается в AuthContext
                    throw new AppError(
                        'Необходима авторизация',
                        401,
                        ErrorCodes.UNAUTHORIZED
                    );
                }

                throw new AppError(
                    responseData?.message || 'Ошибка при получении списка федераций',
                    status,
                    ErrorCodes.INTERNAL_ERROR
                );
            }

            throw new AppError(
                'Ошибка при получении списка федераций',
                500,
                ErrorCodes.INTERNAL_ERROR
            );
        }
    }

    static async deleteFederation(id: number): Promise<void> {
        try {
            // Токены теперь в httpOnly cookies, они отправляются автоматически
            await api.delete<ApiResponse<void>>(
                API_ENDPOINTS.FEDERATIONS.DELETE(id)
            );
        } catch (error) {
            logger.error('Error deleting federation:', error);

            if (axios.isAxiosError(error)) {
                const axiosError = error as AxiosError<any>;

                if (!axiosError.response) {
                    throw new AppError(
                        'Ошибка сети. Проверьте подключение к интернету.',
                        503,
                        ErrorCodes.NETWORK_ERROR
                    );
                }

                const status = axiosError.response.status;
                const responseData = axiosError.response.data;

                if (status === 401) {
                    // Токены в httpOnly cookies, редирект обрабатывается в AuthContext
                    throw new AppError(
                        'Необходима авторизация',
                        401,
                        ErrorCodes.UNAUTHORIZED
                    );
                }

                if (status === 404) {
                    throw new AppError(
                        'Федерация не найдена',
                        404,
                        ErrorCodes.NOT_FOUND
                    );
                }

                throw new AppError(
                    responseData?.message || 'Ошибка при удалении федерации',
                    status,
                    ErrorCodes.INTERNAL_ERROR
                );
            }

            throw new AppError(
                'Ошибка при удалении федерации',
                500,
                ErrorCodes.INTERNAL_ERROR
            );
        }
    }
}

export default FederationService;